<script lang="ts">
	import TextEditorToolbar from './TextEditorToolbar.svelte'
	import { Editor } from '@tiptap/core'
	import { Image } from '@tiptap/extension-image'
	import { Link } from '@tiptap/extension-link'
	import { TextAlign } from '@tiptap/extension-text-align'
	import { Typography } from '@tiptap/extension-typography'
	import { Underline } from '@tiptap/extension-underline'
	import { StarterKit } from '@tiptap/starter-kit'
	import { createEventDispatcher, onDestroy, onMount } from 'svelte'
	import { run } from 'svelte/legacy'

	interface Props {
		name: string
		content: string
		additionalClasses: string
		required?: boolean
		readonly: boolean
	}

	let { name, content, additionalClasses, required = false, readonly }: Props = $props()

	let element: HTMLElement = $state(null)
	let editor: Editor = $state(null)

	const dispatch = createEventDispatcher()

	onMount(() => {
		editor = new Editor({
			editable: !readonly,
			element: element,
			extensions: [
				// sync with allowedTags in `pkg/controllers/settings/update_tac_text.go`
				Underline,
				Link.configure({
					protocols: ['http', 'https', 'mailto'],
					openOnClick: false,
					autolink: true,
				}),
				Image,
				TextAlign.configure({
					types: ['heading', 'paragraph'],
				}),
				Typography,
				StarterKit.configure({
					heading: {
						levels: [1, 2, 3, 4],
					},
				}),
			],
			content: content,
			onTransaction: () => {
				// force re-render so `editor.isActive` works as expected
				editor = editor
			},
			editorProps: {
				attributes: {
					class: additionalClasses.replace(/\s\s+/g, ' ').trim(),
				},
			},
			onUpdate({ editor }) {
				dispatch('input', editor.getHTML())
			},
		})
	})

	onDestroy(() => {
		if (editor) {
			editor.destroy()
		}
	})

	function updateEditorAfterExternalChange(content: string) {
		if (editor && content !== editor.getHTML()) {
			editor.commands.setContent(content)
		}
	}

	run(() => {
		updateEditorAfterExternalChange(content)
	})
</script>

{#if editor && !readonly}
	<TextEditorToolbar {editor} />
	<div class="mt-2"></div>
{/if}

<div class="relative">
	{#if !readonly}
		<!-- native textarea for validation -->
		<textarea
			{name}
			class="absolute inset-0 bg-transparent"
			aria-hidden={true}
			{required}
			tabindex="-1">{content}</textarea
		>
	{/if}
	<div bind:this={element}></div>
</div>
